import { useEagerLogoLoading } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { themeColor } from "@TKCCP/Styles/variables";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface LogoLinksSearchStyles {
    container: InjectableCss;
    logoWrapper: InjectableCss;
    searchMenuWrapper: InjectableCss;
    linkListHidden: HiddenProps;
    richContentGridItem: InjectableCss;
    image?: LazyImageProps;
    img?: ImgProps;
    txt?: InjectableCss;
    customMessageContainer?: InjectableCss;
}

// new addition
const mapStateToProps = (state: ApplicationState) => ({
    homePageLink: getHomePageUrl(state),
    currentPage: getCurrentPage(state),
    settings: getSettingsCollection(state),
});

interface OwnProps extends WidgetProps, PageProps {
    fields: {
        headerText: string;
        headerImage: string;
        isMobileSpecific: boolean;
        headerImageUrl: string;
        mobileSpecificImage: string;
    };
    extendedStyles?: LogoLinksSearchStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;
export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    logoWrapper: {
        css: css`
            text-align: left;
            padding: 20px 40px 20px 45px;
            margin: 0 0 0 4px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-grow: 1;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            img {
                height: 81px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                width: auto;
                                margin: 0;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                height: 100px;
                            `,
                            null,
                        ],
                        "max",
                    );
                }}
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                height: 80px;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },

    searchMenuWrapper: {
        css: css`
            text-align: right;
            padding: 20px 0 20px 45px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                        css`
                            flex-grow: 8;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            padding-right: 45px;
                        `,
                        null,
                    ],
                    "max",
                );
            }}
            a:last-child {
                display: none;
            }
        `,
    },
    linkListHidden: {
        below: "lg",
    },
    richContentGridItem: {
        css: css`
            width: 300px;
        `,
    },
    image: {
        css: css`
            width: 100%;
        `,
    },
    // img: {
    //     css: css`
    //         width: 100%;
    //         height: auto;
    //         max-width: 490px;
    //     `,
    // },
    img: {
    css: css`
        width: 100%;
        height: auto;
        max-width: 490px;
        aspect-ratio: 2054 / 347;
    `,
},
    txt: {
        css: css`
            width: 100%;
            text-align: center;
            color: #003875;
            display: flex;
            font-weight: bold;
            font-size: 25px;
            text-transform: uppercase;
        `,
    },
    customMessageContainer: {
        css: css`
            align-items: flex-end;
            display: flex;
            padding-bottom: 20px;
            padding-right: 5px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            align-items: center;
                            padding-bottom: 0;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
};

const LogoLinksSearch: React.FC<Props> = ({ id, homePageLink, currentPage, fields, settings }: Props) => {
    const isEager = useEagerLogoLoading();
    const isHomePage = currentPage.type === "HomePage";

    return (
        <StyledWrapper {...logoLinksSearchStyles.container}>
            <StyledWrapper {...logoLinksSearchStyles.logoWrapper}>
                <Zone contentId={id} zoneName="Logo" fixed />
            </StyledWrapper>
            {/* CPSB - 117 : Added Image and text for Header */}
            <StyledWrapper {...logoLinksSearchStyles.customMessageContainer}>
                {isHomePage && <StyledWrapper {...logoLinksSearchStyles.txt}> {fields.headerText} </StyledWrapper>}
                {!isHomePage && (
                    <Link href={fields.headerImageUrl}>

  <Img
    src={fields.headerImage}
    loading={isEager ? "eager" : "lazy"}
    altText={translate("Home")}
    width={2054}
    height={347}
    {...logoLinksSearchStyles.img}
/>



                    </Link>
                )}
            </StyledWrapper>
            <StyledWrapper {...logoLinksSearchStyles.searchMenuWrapper}>
                <Hidden {...logoLinksSearchStyles.linkListHidden}>
                    <Zone contentId={id} zoneName="LinkList" fixed />
                </Hidden>
                <Zone contentId={id} zoneName="Search" fixed />
            </StyledWrapper>
            <StyledWrapper>
                <script
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: `
    window.__lc = window.__lc || {};
    window.__lc.license = ${JSON.stringify(settings?.liveChatSetting)};
    ;(function(n,t,c){function i(n){return e._h?e._h.apply(null,n):e._q.push(n)}var e={_q:[],_h:null,_v:"2.0",on:function(){i(["on",c.call(arguments)])},once:function(){i(["once",c.call(arguments)])},off:function(){i(["off",c.call(arguments)])},get:function(){if(!e._h)throw new Error("[LiveChatWidget] You can't use getters before load.");return i(["get",c.call(arguments)])},call:function(){i(["call",c.call(arguments)])},init:function(){var n=t.createElement("script");n.async=!0,n.type="text/javascript",n.src="https://cdn.livechatinc.com/tracking.js",t.head.appendChild(n)}};!n.__lc.asyncInit&&e.init(),n.LiveChatWidget=n.LiveChatWidget||e}(window,document,[].slice))
    `,
                    }}
                ></script>
            </StyledWrapper>
        </StyledWrapper>
    );
};
const logoLinksSearch: WidgetModule = {
    component: connect(mapStateToProps)(LogoLinksSearch),
    definition: {
        group: "Common",
        icon: "magnifying-glass",
        fieldDefinitions: [
            {
                name: "headerText",
                fieldType: "Translatable", // Spire provides quite a few editorTemplates out-of-the-box.
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: "headerImage",
                displayName: "Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: "isMobileSpecific",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
            },
            {
                name: "mobileSpecificImage",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.isMobileSpecific,
            },
            {
                fieldType: "General",
                name: "headerImageUrl",
                displayName: "Image Redirect Url",
                editorTemplate: "TextField",
                defaultValue: "",
                validate: value =>
                    value.startsWith("https") ? null : "Please enter a valid url (starting with https)",
                tooltip:
                    " Please enter full url.  Ex: https:// www.courtesy products.com /Catalog/amenities /combs-And-brushes /wrapped-hair-brush",
            },
        ],
    },
};

export default logoLinksSearch;
